/**
 * The board copy both sides need to agree on: the three steps a fresh page
 * starts with.
 */

/** "How it works", as every new page starts. */
export const BOARD_STEPS_ROWS: ReadonlyArray<readonly [string, string, string]> = [
  ["1", "Save your seat", "Takes ten seconds."],
  ["2", "Your link arrives", "A private page, by email."],
  ["3", "Watch when you like", "Replay any time."],
];

/** "How it works" on a live's page: the last line is when it happens ("Thursday at 7:00 PM ET."). */
export function liveStepsRows(whenShort: string): Array<[string, string, string]> {
  return [
    ["1", "Save your seat", "Takes ten seconds."],
    ["2", "Your link and a calendar invite arrive", "By email, right away."],
    ["3", "Join live, or catch the replay", whenShort ? `${whenShort}.` : "At the time above."],
  ];
}

/** The left of a live page's top line; the right is the day and time. */
export const LIVE_TOP_LEFT = "Free live session";

/**
 * The steps one read of the talk lands, in the order the waiting stage plays
 * them. `read` is the transcript being parsed; the other eight are the producer
 * capabilities, each counted as landed whether it answered or fell back.
 *
 * Shared because both sides need the same order: the read route appends to
 * `webinar_boards.read_progress` as each lands, and the stage advances one scene
 * per landed step. The client cannot import from `workers/`, so it lives here
 * and `workers/src/ai/producer-types.ts` re-exports it.
 */
export const READ_STEPS = [
  "read",
  "title",
  "desc",
  "chapters",
  "quotes",
  "faq",
  "moments",
  "offer",
  "followups",
] as const;

export type ReadStep = (typeof READ_STEPS)[number];

/**
 * The tenth scene: the cards laid out on the page. It is not a capability, so it
 * is never handed to `onStep`; the read route appends it once the board has been
 * materialised. `SCENE_ORDER` is the fixed order the stage plays.
 */
export const READ_PAGE_STEP = "page";
export type SceneStep = ReadStep | typeof READ_PAGE_STEP;
export const SCENE_ORDER: readonly SceneStep[] = [...READ_STEPS, READ_PAGE_STEP];

/**
 * A live's page is written from the host's notes in fewer steps: the notes read,
 * the title, the description, what the live will cover (the chapters card), and
 * the page laid out.
 */
export const LIVE_SCENE_ORDER: readonly SceneStep[] = ["read", "title", "desc", "chapters", READ_PAGE_STEP];

/** The direction a page took from a picture the host gave, not from the deck. */
export const INSPIRATION_DIRECTION = "inspiration";

/**
 * The page directions' names, keyed exactly as `workers/src/ai/skills/pages.ts` keys its DIRECTIONS
 * record, plus the one name that is not a deck key: the client only ever needs the name to show on
 * a landed page's chrome. The last twelve keys are the print deck pages were drawn from before the
 * new page maker: rows written then still store them, so their names stay readable.
 */
export const DIRECTION_NAMES: Record<string, string> = {
  [INSPIRATION_DIRECTION]: "From your picture",
  keynote: "Keynote",
  studio_dark: "Studio dark",
  creator: "Creator",
  clean_saas: "Clean SaaS",
  event: "Event",
  editorial: "Editorial spread",
  poster: "Poster",
  cinema: "Cinema",
  quiet: "Quiet",
  brochure: "Brochure",
  split: "Split screen",
  timeline: "Timeline",
  notebook: "Notebook",
  broadsheet: "Broadsheet",
  studio: "Studio",
  letter: "Letter",
  ledger: "Ledger",
};
